import { Injectable } from '@angular/core';
import { AppSettingsService } from './app-settings.service';

/** Interface for a feature toggle */
export interface IFeatureToggle {
  /** The id of the feature toggle */
  id: string;

  /** A boolean value indicating whether the feature toggle is enabled */
  isEnabled: boolean;
}

/** Feature toggle service */
@Injectable({ providedIn: 'root' })
export class FeatureToggleService {
  private reportedErrors: Set<string> = new Set();

  public get isSettingsLoaded() {
    return this.appSettingsService.isSettingsLoaded;
  }

  /** Dictionary of feature toggles keyed on lower-cased feature toggle id */
  private featureToggles: { [id: string]: IFeatureToggle };

  constructor(private appSettingsService: AppSettingsService) {
    this.featureToggles = {};
    this.loadFeatureToggles();
  }

  /** See: IFeatureToggleService.isEnabled */
  public isEnabled = (featureToggleId: string): boolean => {
    // check if FT is loaded from appSettingsService
    if (Object.keys(this.featureToggles).length === 0) {
      if (
        this.appSettingsService &&
        this.appSettingsService.featureToggles &&
        this.appSettingsService.featureToggles.length !== 0
      ) {
        // if appSettingService has is loaded from the server, load FTs here
        this.loadFeatureToggles();
      } else {
        if (!this.reportedErrors.has(featureToggleId)) {
          this.reportedErrors.add(featureToggleId);
          console.error(`AppSettings not loaded, no feature toggles found for ${featureToggleId}`);
        }
        return false;
      }
    }

    const lowerCaseToggleId = featureToggleId.toLowerCase();
    const isEnabled = lowerCaseToggleId in this.featureToggles && this.featureToggles[lowerCaseToggleId].isEnabled;
    return !!isEnabled;
  };

  public loadFeatureToggles = () => {
    // Load the toggles from appSettingsService
    this.appSettingsService.featureToggles.forEach((value: IFeatureToggle, key: number) => {
      this.featureToggles[value.id.toLowerCase()] = value;
    });
  };
}
